import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { getEnv } from '../../../../../../helpers/getEnv';
import { AgentMessage, AgentPart, Message, UiComponent } from './clientMessages';
import { uiComponentSchema } from './uiComponentsSchemas';
import { usePropertyPageQuery } from '../../../../../graphql/usePropertyPageQuery';
import { createInitialMessages } from '../helpers/createInitialMessage';
import { isNotNullOrUndefinedValue } from '../../../../../../helpers/isNotNullOrUndefinedValue';

const EXPERIENCE_API_URL = getEnv.PUBLIC_EXPERIENCE_API_URL();

const sessionEventSchema = z.object({ session_id: z.string() });
const toolCallEventSchema = z.object({
  invocation_id: z.string(),
  tool: z.string(),
  finished: z.boolean(),
});
const contentEventSchema = z.object({
  invocation_id: z.string(),
  components: z.array(uiComponentSchema),
});

const parseSseEvent = (line: string): { event?: string; data?: string } => {
  if (line.startsWith('event:')) {
    return { event: line.slice(6).trim() };
  }
  if (line.startsWith('data:')) {
    return { data: line.slice(5).trim() };
  }
  return {};
};

interface SSEEventHandler {
  onSession?: (sessionId: string) => void;
  onToolCall?: (tool: string, finished: boolean, invocationId: string) => void;
  onContent?: (components: UiComponent[], invocationId: string) => void;
  onDone?: () => void;
  onError?: () => void;
}

const createServerSentEventProcessor = (
  invocationId: string,
  handlers: SSEEventHandler,
  currentSessionId: string | null,
) => {
  return async (eventType: string, data: string): Promise<void> => {
    if (data === '[DONE]') return;

    try {
      const parsed = JSON.parse(data) as unknown;

      switch (eventType) {
        case 'session': {
          const sessionResult = sessionEventSchema.safeParse(parsed);
          if (sessionResult.success && !isNotNullOrUndefinedValue(currentSessionId)) {
            handlers.onSession?.(sessionResult.data.session_id);
          }
          break;
        }

        case 'tool_call': {
          const toolCallResult = toolCallEventSchema.safeParse(parsed);
          if (toolCallResult.success) {
            const { tool, finished } = toolCallResult.data;
            handlers.onToolCall?.(tool, finished, invocationId);
          }
          break;
        }

        case 'content': {
          const contentResult = contentEventSchema.safeParse(parsed);
          if (contentResult.success) {
            const { components } = contentResult.data;
            handlers.onContent?.(components, invocationId);
          }
          break;
        }

        case 'done': {
          handlers.onDone?.();
          break;
        }
      }
    } catch {
      handlers.onError?.();
    }
  };
};

const createServerSentEventsReader = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  eventProcessor: (eventType: string, data: string) => Promise<void>,
  abortController: AbortController,
): Promise<void> => {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';

  const abortPromise = new Promise<never>((_, reject) => {
    abortController.signal.addEventListener('abort', () => {
      reject(new Error('Stream aborted'));
    });
  });

  // Check if the abort signal is already triggered
  if (abortController.signal.aborted) {
    return;
  }

  try {
    // Convert reader to async iterable
    const readChunks = async function* () {
      let result = await reader.read();
      while (!result.done) {
        yield result.value;
        result = await reader.read();
      }
    };

    // Ends stream processing early if the abort signal is triggered
    await Promise.race([
      (async () => {
        for await (const chunk of readChunks()) {
          buffer += decoder.decode(chunk, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            const { event, data } = parseSseEvent(trimmedLine);

            if (event) {
              currentEvent = event;
            } else if (data && currentEvent) {
              await eventProcessor(currentEvent, data);
              if (currentEvent === 'done') {
                return;
              }
            }
          }
        }
      })(),
      abortPromise,
    ]);
  } catch {
    return;
  }
};

const createStreamRequest = async (
  url: string,
  messageText: string,
  sessionId: string | null,
  abortController: AbortController,
): Promise<Response> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    },
    credentials: 'include',
    body: JSON.stringify({
      message: messageText,
      sessionId: sessionId ?? undefined,
    }),
    signal: abortController.signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  return response;
};

export const useChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const propertyData = usePropertyPageQuery()?.data?.propertyPageBySlugV3.pcaChatContext;

  useEffect(() => {
    if (propertyData && !sessionId) {
      setMessages((m) => m.concat(createInitialMessages({ pcaChatContext: propertyData })));
    }
  }, [propertyData, sessionId]);

  const upsertAgentMessage = useCallback((invocationId: string, part: AgentPart) => {
    setMessages((prev) => {
      const index = prev.findIndex(
        (message): message is AgentMessage =>
          (message as AgentMessage).invocationId === invocationId,
      );
      if (index !== -1) {
        const existing = prev[index] as AgentMessage;
        const updated = {
          ...existing,
          parts: [...existing.parts, part],
        };
        const copy = [...prev];
        copy[index] = updated;
        return copy;
      }
      return prev.concat({
        sender: 'agent',
        invocationId,
        parts: [part],
      } satisfies AgentMessage);
    });
  }, []);

  const reportStreamError = useCallback(() => {
    setMessages((m) =>
      m.concat({
        sender: 'agent',
        invocationId: crypto.randomUUID(),
        parts: [
          {
            type: 'content',
            components: [
              {
                componentType: 'text',
                text: `Oops, something went wrong. Please retry.`,
              },
              {
                componentType: 'user_action',
                actions: ['Retry'],
              },
            ],
          },
        ],
      }),
    );
  }, []);

  const streamChat = useCallback(
    async (messageText: string, echoUser: boolean, visibility: 'visible' | 'hidden') => {
      setLoading(true);

      const agentInvocationId = crypto.randomUUID();
      let abortController: AbortController | null = new AbortController();

      if (echoUser) {
        setMessages((m) =>
          m.concat({
            invocationId: crypto.randomUUID(),
            sender: 'user',
            text: messageText,
            visibility,
          }),
        );
      }

      upsertAgentMessage(agentInvocationId, {
        type: 'content',
        components: [],
      });

      try {
        const response = await createStreamRequest(
          `${EXPERIENCE_API_URL}/api/chat/stream`,
          messageText,
          sessionId,
          abortController,
        );

        const reader = response.body!.getReader();

        const eventHandlers: SSEEventHandler = {
          onSession: (newSessionId) => setSessionId(newSessionId),
          onToolCall: (tool, finished, invocationId) => {
            upsertAgentMessage(invocationId, {
              type: 'tool_call',
              tool,
              finished,
            });
          },
          onContent: (components, invocationId) => {
            upsertAgentMessage(invocationId, {
              type: 'content',
              components,
            });
          },
          onDone: () => {},
          onError: () => {
            reportStreamError();
          },
        };

        const eventProcessor = createServerSentEventProcessor(
          agentInvocationId,
          eventHandlers,
          sessionId,
        );

        await createServerSentEventsReader(reader, eventProcessor, abortController);
      } catch {
        reportStreamError();
      } finally {
        setLoading(false);
        abortController = null;
      }
    },
    [reportStreamError, sessionId, upsertAgentMessage],
  );

  const executeChatCall = useCallback(
    (text: string) => streamChat(text, true, 'visible'),
    [streamChat],
  );

  const appendChat = useCallback(
    (text: string, visibility: 'visible' | 'hidden') => streamChat(text, false, visibility),
    [streamChat],
  );

  const handleSubmit = useCallback(
    (input: string) => {
      if (!input.trim()) return;
      void executeChatCall(input);
    },
    [executeChatCall],
  );

  return {
    messages,
    sessionId,
    loading,
    handleSubmit,
    appendChat,
    executeChatCall,
  };
};
